import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';

import { TranslationService } from '../../../core/i18n/translation.service';
import { UserDto } from '../../users/models/user-admin.models';
import { UserAdminApi } from '../../users/services/user-admin.api';
import { IssueDto, IssueLocationDto, IssuePriority, IssueRoleDto } from '../models/issue.models';
import { IssuesApi } from '../services/issues.api';

type IssueViewMode = 'list' | 'tiles';

const ISSUE_VIEW_STORAGE_KEY = 'junoaudit.issues.view-mode';

@Component({
  selector: 'app-issue-list',
  imports: [DatePipe, NgClass],
  templateUrl: './issue-list.component.html',
  styleUrl: './issue-list.component.css',
})
export class IssueListComponent {
  private readonly api = inject(IssuesApi);
  private readonly usersApi = inject(UserAdminApi);
  private loadRequestId = 0;

  protected readonly i18n = inject(TranslationService);
  readonly isActive = input(false);
  readonly searchQuery = input('');
  readonly reloadRequest = input(0);
  readonly editRequested = output<IssueDto>();

  protected readonly isLoading = signal(false);
  protected readonly loadError = signal('');
  protected readonly issues = signal<IssueDto[]>([]);
  protected readonly locations = signal<IssueLocationDto[]>([]);
  protected readonly roles = signal<IssueRoleDto[]>([]);
  protected readonly users = signal<UserDto[]>([]);
  protected readonly viewMode = signal<IssueViewMode>(this.restoreViewMode());
  protected readonly visibleIssues = computed(() => {
    const query = this.searchQuery().trim().toLocaleLowerCase();
    if (!query) {
      return this.issues();
    }

    return this.issues().filter((issue) => {
      const searchableValues = [
        issue.title,
        issue.description,
        issue.priority,
        this.getPriorityLabel(issue.priority),
        issue.status,
        this.getStatusLabel(issue),
        this.getReporterName(issue.reporter_user_code),
        this.getLocationName(issue.location_id),
        this.getRoleName(issue.assigned_role_id),
      ];
      return searchableValues.some((value) => value.toLocaleLowerCase().includes(query));
    });
  });

  constructor() {
    effect(() => {
      const active = this.isActive();
      this.reloadRequest();
      if (active) {
        void this.loadIssues();
      }
    });
  }

  protected setViewMode(mode: IssueViewMode): void {
    if (this.viewMode() === mode) {
      return;
    }

    this.viewMode.set(mode);
    localStorage.setItem(ISSUE_VIEW_STORAGE_KEY, mode);
  }

  protected trackByIssueId(_index: number, issue: IssueDto): string {
    return issue.id;
  }

  protected getPriorityLabel(priority: IssuePriority): string {
    return this.i18n.t(`issueList.priority.${priority}`);
  }

  protected getPriorityClass(priority: IssuePriority): Record<string, boolean> {
    return {
      'issue-list__priority--low': priority === 'low',
      'issue-list__priority--medium': priority === 'medium',
      'issue-list__priority--critical': priority === 'critical',
    };
  }

  protected getStatusLabel(issue: IssueDto): string {
    return this.i18n.t(`issueList.status.${issue.status}`);
  }

  protected getStatusClass(issue: IssueDto): Record<string, boolean> {
    return {
      'app-status-chip--draft': issue.status === 'new',
      'issue-list__status--in-progress': issue.status === 'in_progress',
      'app-status-chip--active': issue.status === 'resolved',
      'issue-list__status--rejected': issue.status === 'rejected',
      'app-status-chip--archived': issue.status === 'archived',
    };
  }

  protected getLocationName(locationId: string): string {
    return (
      this.locations().find(({ id }) => id === locationId)?.name ??
      this.i18n.t('issueList.fallbacks.unknownLocation')
    );
  }

  protected getRoleName(roleId: number | null): string {
    if (roleId === null) {
      return this.i18n.t('issueList.fallbacks.unassigned');
    }
    return (
      this.roles().find(({ id }) => id === roleId)?.name ??
      this.i18n.t('issueList.fallbacks.unassigned')
    );
  }

  protected getReporterName(userCode: string): string {
    const user = this.users().find(({ user_code }) => user_code === userCode);
    if (!user) {
      return '—';
    }
    return [user.first_name, user.last_name]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(' ') || '—';
  }

  protected editIssue(issue: IssueDto): void {
    this.editRequested.emit(issue);
  }

  private async loadIssues(): Promise<void> {
    const requestId = ++this.loadRequestId;
    this.isLoading.set(true);
    this.loadError.set('');

    try {
      const response = await firstValueFrom(
        forkJoin({
          issues: this.api.getIssues(),
          locations: this.api.getLocations(),
          roles: this.api.getRoles(),
          users: this.usersApi.listUsers().pipe(catchError(() => of([]))),
        }),
      );
      if (requestId !== this.loadRequestId) {
        return;
      }

      this.issues.set(response.issues);
      this.locations.set(response.locations.results);
      this.roles.set(response.roles.results);
      this.users.set(response.users);
    } catch {
      if (requestId === this.loadRequestId) {
        this.loadError.set(this.i18n.t('issueList.errors.load'));
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading.set(false);
      }
    }
  }

  private restoreViewMode(): IssueViewMode {
    return localStorage.getItem(ISSUE_VIEW_STORAGE_KEY) === 'tiles' ? 'tiles' : 'list';
  }
}
